<template>
	<ModalHeader
		:title="getString('pos_tips_title')"
		:button-text="getString('save')"
		:button-loading="savingConfig"
		@click-button="saveTerminalTipsSettings"/>

	<div class="tips-desc-container mt-20 mb-15">
		<p class="title">{{getString("pos_content_title")}}</p>
		<p class="explain">{{getString("pos_content_explain")}}</p>
	</div>

	<div class="tips-container">
		<div class="percentage-tips">
			<AppNumberInput ref="percentage-1" symbol="%" right-side-symbol :min="1" :max="100" :value="10"/>
			<AppNumberInput ref="percentage-2" symbol="%" right-side-symbol :min="1" :max="100" :value="15"/>
			<AppNumberInput ref="percentage-3" symbol="%" right-side-symbol :min="1" :max="100" :value="20"/>
		</div>
		<div class="exception-container pt-7">
			<AppList :items="tipExceptions">
				<template #amount-tips="{item}">
					<AppList :items="item.childrens"/>
					<div class="fixed-amount-container">
						<p>{{getString('fixed_amount_explain')}}</p>
						<div class="tips">
							<AppNumberInput ref="fixed-1" :symbol="currencyStore.currCurrency.symbol" :double-decimal="currencyStore.currCurrency.decimals === 2" :min="0.01" :value="1"/>
							<AppNumberInput ref="fixed-2" :symbol="currencyStore.currCurrency.symbol" :double-decimal="currencyStore.currCurrency.decimals === 2" :min="0.01" :value="2"/>
							<AppNumberInput ref="fixed-3" :symbol="currencyStore.currCurrency.symbol" :double-decimal="currencyStore.currCurrency.decimals === 2" :min="0.01" :value="3"/>
						</div>
					</div>
				</template>
			</AppList>
		</div>
	</div>

	<div class="reset-container">
		<p>{{getString('reset_title')}}</p>
		<AppButton :text="getString('reset')" outline small @click="resetTerminal"/>
	</div>

	<AppPopup
		ref="reset-terminal-tips"
		:title="getString('reset_terminal_tips_title',[terminal ? terminal.name : ''])"
		:body-text="getString('reset_terminal_tips_explain')"
		:buttons="resetTipsButtons"/>
</template>

<script>
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppList from "@/shared/components/AppList.vue";
import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";
import AppPopup from "@/shared/components/AppPopup.vue";
import {useUserStore} from "@/user/stores";
import AppButton from "@/shared/components/AppButton.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, userStore, currencyStore, showNotification: notificationStore.showNotification};
	},
	props: {
		terminalId: {
			type: Number,
			default: null
		}
	},
	emits: ['terminal-updated'],
	data() {
		return {
			smartTipThreshold: 10,
			savingConfig: false,
			terminalSmartTips: false,
			terminalResetting: false
		}
	},
	name: "TerminalTipsSettings" ,
	components: {AppButton, AppPopup, AppList, ModalHeader, AppBottomSheet, AppNumberInput},
	computed: {
		tipExceptions() {
			return [
				{
					title: this.getString('pos_exceptions'),
					switch: true,
					active: this.terminalSmartTips,
					callback: () => this.terminalSmartTips = !this.terminalSmartTips,
					dropdownSlot: 'amount-tips',
					childrens: [
						{
							title: this.getString('threshold_title'),
							explain: this.getString('threshold_explain'),
							numberInput: true,
							callbackNumber: (value) => this.smartTipThreshold = value,
							symbol: this.currencyStore.currCurrency.symbol,
							value: 10,
							min: 5
						}
					]
				}
			]
		},
		resetTipsButtons() {
			return [
				{
					text: this.getString('cancel'),
					negative: true
				},
				{
					text: this.getString('reset'),
					loading: this.terminalResetting,
					callback: () => {
						this.terminalResetting = true;
						this.userStore.updateBusinessTerminalConfiguration(this.terminalId, {reset: 1}, (result) => {
							this.$emit('terminal-updated');
							this.terminalResetting = false;
							this.$refs['reset-terminal-tips'].toggleStatus(false);

							if(result){
								this.showNotification('tips-saved');
							}else {
								this.showNotification('generic-fail');
							}
						});
					}
				}
			]
		},
		terminal() {
			let terminalInstance = null;

			if(this.terminalId && this.userStore.businessTerminals !== null) {
				let terminal = this.userStore.businessTerminals.find(t => parseInt(t.terminal_id) === this.terminalId)
				if(terminal) {
					terminalInstance = terminal;
				}
			}

			return terminalInstance;
		}
	},
	methods: {
		saveTerminalTipsSettings() {
			let tips = {
				'percentages': [
					this.$refs['percentage-1'].currentValue,
					this.$refs['percentage-2'].currentValue,
					this.$refs['percentage-3'].currentValue,
				]
			};
			let notUniqueTips = tips.percentages[0] === tips.percentages[1] || tips.percentages[0] === tips.percentages[2] || tips.percentages[1] === tips.percentages[2];

			tips.percentages = tips.percentages.sort((a, b) => b - a);

			if(this.terminalSmartTips){
				tips['smart_tip_threshold'] = this.currencyStore.actualToDbValue(this.smartTipThreshold);
				tips['fixed_amounts'] = [
					this.currencyStore.actualToDbValue(this.$refs['fixed-1'].currentValue),
					this.currencyStore.actualToDbValue(this.$refs['fixed-2'].currentValue),
					this.currencyStore.actualToDbValue(this.$refs['fixed-3'].currentValue),
				];
				tips.fixed_amounts.sort((a, b) => b - a);

				if(!notUniqueTips){
					notUniqueTips = tips.fixed_amounts[0] === tips.fixed_amounts[1] || tips.fixed_amounts[0] === tips.fixed_amounts[2] || tips.fixed_amounts[1] === tips.fixed_amounts[2];
				}
			}

			if(notUniqueTips){
				this.showNotification('not-unique-tips');
				return;
			}

			this.savingConfig = true;
			this.userStore.updateBusinessTerminalConfiguration(this.terminalId, tips, (result) => {
				this.savingConfig = false;
				this.$emit('terminal-updated');

				if(result){
					this.showNotification('tips-saved')
				}else {
					this.showNotification('generic-fail');
				}
			});
		},
		resetTerminal() {
			this.$refs['reset-terminal-tips'].toggleStatus(true);
		}
	}
}
</script>

<style scoped lang="scss">
.tips-desc-container {
	display: flex;
	padding: 0px 15px;
	flex-direction: column;
	align-items: flex-start;
	gap: 15px;
	align-self: stretch;

	.title {
		font-size: 16px;
		font-weight: 500;
	}

	.explain {
		color: #818181;
		font-size: 13px;
		font-style: normal;
		font-weight: 500;
		line-height: normal;
	}

}
.tips-container {
	border-radius: 13px;
	background: var(--pb-alpha-1, rgba(7, 82, 163, 0.10));
	display: flex;
	padding: 15px;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: 15px;
	align-self: stretch;

	.percentage-tips {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		gap: 40px;
		align-self: stretch;
		padding-right: 12px;

		@media (max-width: 460px) {
			flex-direction: column;
			align-items: flex-end;
			gap: 15px;
		}
	}
}
.exception-container {
	display: flex;
	justify-content: center;
	align-items: center;
	align-self: stretch;
	border-top: 1px solid var(--pb-alpha-4, rgba(7, 82, 163, 0.40));

	.fixed-amount-container {
		border-top: 1px solid var(--pb-alpha-4, rgba(7, 82, 163, 0.40));
		padding: 15px;
		.tips {
			display: flex;
			justify-content: flex-end;
			align-items: center;
			gap: 40px;
			align-self: stretch;
			padding-top: 15px;

			@media (max-width: 512px) {
				flex-direction: column;
				gap: 15px;
				align-items: flex-end;
			}
		}
	}
}
.reset-container {
	margin-top: 8px;
	padding: 8px 12px;
	display: flex;
	justify-content: space-between;
	align-items: center;

	p {
		padding-right: 15px;
	}
}
</style>